import { useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, RefreshCw, Trash2 } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { ADMIN_QUERY_KEYS } from "@/lib/api/query-options";
import { fetchTrashDashboard, fetchTrashEntities, type TrashEntityInfo } from "@/lib/api/trash";
import { TrashTable } from "@/components/admin/recycle-bin/TrashTable";
import { TrashEntityNav } from "@/components/admin/recycle-bin/TrashEntityNav";
import { RecycleBinSummary } from "@/components/admin/recycle-bin/RecycleBinSummary";

export const Route = createFileRoute("/_authenticated/admin/recycle-bin")({
  /**
   * The active tab lives in the URL so it is deep-linkable and survives a refresh — with eighteen
   * entity types, "the one I was looking at" is worth not losing after every restore. No other page
   * in the app does this yet; it earns its keep here.
   */
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    tab: typeof search.tab === "string" ? search.tab : undefined,
  }),
  component: RecycleBinPage,
});

function RecycleBinPage() {
  const { hasPermission } = useAuth();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();

  // Checked before any data hook, so there are no conditional hooks below it.
  const canView = hasPermission("trash.view");

  const entities = useQuery({
    queryKey: ADMIN_QUERY_KEYS.trashEntities,
    queryFn: fetchTrashEntities,
    enabled: canView,
  });

  /**
   * The retention clock, which the tab list does not carry.
   *
   * A separate query rather than a field on the entities call because it is the same endpoint the
   * dashboard widget already uses, under the same key — so the two views share one cache entry and
   * one invalidation instead of disagreeing about how full the bin is.
   */
  const dashboard = useQuery({
    queryKey: ADMIN_QUERY_KEYS.trashDashboard,
    queryFn: fetchTrashDashboard,
    enabled: canView,
  });

  // Memoised rather than `entities.data ?? []` inline: a fresh array literal on every render would make
  // the memo below recompute every time, defeating it.
  const list = useMemo<TrashEntityInfo[]>(() => entities.data ?? [], [entities.data]);

  // The backend is the source of truth for which tabs exist, so a nineteenth soft-deletable entity
  // appears here the moment its descriptor is registered — no frontend change.
  const active = useMemo<TrashEntityInfo | undefined>(
    () => list.find((entity) => entity.slug === tab) ?? list[0],
    [list, tab],
  );

  if (!canView) {
    return (
      <AccessDenied message="The Recycle Bin is limited to administrators who can manage deleted records." />
    );
  }

  const totalDeleted = list.reduce((sum, entity) => sum + entity.deletedCount, 0);
  const isRefreshing = entities.isFetching || dashboard.isFetching;

  /** One prefix invalidation covers the tab counts, the retention figures and every open grid. */
  function refreshAll() {
    void queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.trashRoot });
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Trash2 className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
            Recycle Bin
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Deleted records are hidden from the platform but kept here. Restore what was removed by
            mistake, or delete it permanently once you are certain.
          </p>
        </div>

        <Button variant="outline" size="sm" onClick={refreshAll} disabled={isRefreshing}>
          <RefreshCw
            className={`mr-2 h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
            aria-hidden="true"
          />
          Refresh
        </Button>
      </header>

      {/* Shown above the error branch below on purpose: if the tab list fails but the dashboard does
          not, "142 records, 9 expiring" is still the more useful half of the page. */}
      {!entities.isLoading && (
        <RecycleBinSummary
          dashboard={dashboard.data}
          isLoading={dashboard.isLoading}
          fallbackTotal={totalDeleted}
        />
      )}

      {entities.isError ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-12 text-center">
          <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">Could not load the Recycle Bin.</p>
          <Button variant="outline" size="sm" onClick={() => entities.refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            Retry
          </Button>
        </div>
      ) : entities.isLoading ? (
        <div className="grid gap-6 md:grid-cols-[240px_1fr]">
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} className="h-9 w-full" />
            ))}
          </div>
          <Skeleton className="h-72 w-full" />
        </div>
      ) : totalDeleted === 0 ? (
        <EmptyState
          icon={Trash2}
          title="Recycle Bin is empty"
          description="Nothing has been deleted. Anything removed from the platform will appear here."
        />
      ) : (
        <div className="grid items-start gap-6 md:grid-cols-[240px_1fr]">
          <TrashEntityNav
            entities={list}
            activeSlug={active?.slug}
            onSelect={(slug) => navigate({ search: { tab: slug } })}
          />

          <section aria-live="polite" className="min-w-0">
            {active ? (
              // Keyed by slug so switching tabs remounts the table: search text, filters, sort and
              // selection belong to the tab they were made in, and carrying a selection across entity
              // types would let a bulk action target ids from a different table.
              <TrashTable key={active.slug} entity={active} />
            ) : null}
          </section>
        </div>
      )}
    </div>
  );
}
